import { clear, remove } from '../helpers/array';
import { WrapperGS } from './WrapperGS';

export interface Disposable { dispose(): any }
export interface Handler<T> { (event?: T): any }
export interface HandlerAsync<T> { (event?: T): Promise<any> }

export class EventGSHandlerDisposable implements Disposable {
    constructor(wrapper: WrapperGS, event: string, eventHandler: (event: any) => void) {
        this.wrapper = wrapper;
        this.event = event;
        this.eventHandler = eventHandler
        this.wrapper.el.addEventListener(event, eventHandler);
    }
    public readonly wrapper: WrapperGS;
    public readonly eventHandler: (event: any) => void;
    public readonly event: string;
    public dispose() { this.wrapper.el.removeEventListener(this.event, this.eventHandler); }
}

/**Return private invoke function ouside to initializer*/
export class EventGSToken<T = unknown> {    

    private readonly _hasHandlersCallback: (() => void) | undefined;
    /**     
     * @param hasHandlersCallback  Invoke then event add first handler. Use for assign pipe listeners only when it need (another one start listen)
     */
    constructor(hasHandlersCallback?: () => void) {
        this._hasHandlersCallback = hasHandlersCallback;
    }

    public invoke: Handler<T> = () => {
        throw new Error("EventGSToken must be attached to EventGS before invocation.");
    };
    /** Invoke then event add first handler. Use for assign pipe listeners only when it need (another one start listen)*/
    public get hasHandlersCallback() { return this._hasHandlersCallback; }
}

/**Return private invoke function ouside to initializer*/
export class EventAsyncGSToken<T = unknown> {

    private readonly _hasHandlersCallback: (() => void) | undefined;
    /**
    * @param hasHandlersCallback  Invoke then event add first handler. Use for assign pipe listeners only when it need (another one start listen)
    */
    constructor(hasHandlersCallback?: () => void) {
        this._hasHandlersCallback = hasHandlersCallback;
    }
    public invoke: HandlerAsync<T> = async () => {
        throw new Error("EventAsyncGSToken must be attached to EventAsyncGS before invocation.");
    };
    /** Invoke then event add first handler. Use for assign pipe listeners only when it need (another one start listen)*/
    public get hasHandlersCallback() { return this._hasHandlersCallback; }
}

export class EventGS<T = unknown>
{
    private readonly _handlers:Handler<T>[]=[];
    private readonly _handlersOnce:Handler<T>[]=[];
    private _sourceInitialized = false;

    private readonly _token: EventGSToken<T>;
    constructor(token: EventGSToken<T>) {
        this._token = token;
        this._token.invoke = (e)=> this.invoke(e);
    }

    addHandler(listener: Handler<T>): Disposable {
        this.checkFirstHandler()            
        this._handlers.push(listener);
        return { dispose: () => this.removeHandler(listener) };
    }
    addOnceHandler(listener: Handler<T>): Disposable {
        this.checkFirstHandler()
        this._handlersOnce.push(listener);
        return { dispose: () => this.removeHandler(listener) };
    }

    removeHandler(handler: Handler<T>) {
        remove(this._handlers, handler);
        remove(this._handlersOnce, handler);
    }

    private invoke(event?: T) {
        [...this._handlers].forEach((l) => l(event));
        const onceList = [...this._handlersOnce];
        clear(this._handlersOnce);
        onceList.forEach((l) => l(event));
    }

    private checkFirstHandler() {
        if (this._sourceInitialized) return;
        this._token.hasHandlersCallback?.();
        this._sourceInitialized = true;
    }
}

export class EventAsyncGS<T = unknown>
{
    private _handlers:HandlerAsync<T>[] = [];
    private _handlersOnce:HandlerAsync<T>[]=[];
    private _sourceInitialized = false;

    private readonly _token: EventAsyncGSToken<T>;
    constructor(token: EventAsyncGSToken<T>) {
        this._token = token;
        this._token.invoke = async (e) => await this.invokeAsync(e);
    }

    addHandler(listener: HandlerAsync<T>): Disposable {
        this.checkFirstHandler();
        this._handlers.push(listener);
        return { dispose: () => this.removeHandler(listener) };
    }
    addOnceHandler(listener: HandlerAsync<T>): Disposable {
        this.checkFirstHandler();
        this._handlersOnce.push(listener);
        return { dispose: () => this.removeHandler(listener) };
    }

    removeHandler(handler: HandlerAsync<T>) {
        remove(this._handlers, handler);
        remove(this._handlersOnce, handler);
    }

    private async invokeAsync(event?: T) {
        for (const handler of [...this._handlers]) await handler(event);
        const onceList = [...this._handlersOnce];
        clear(this._handlersOnce);
        for (const handler of onceList) await handler(event);
    }
    private checkFirstHandler() {
        if (this._sourceInitialized) return;
        this._token.hasHandlersCallback?.();
        this._sourceInitialized = true;
    }
}
